import type { JSX } from 'react';
import { Dialog } from '../../components/Dialog';
import { useProject } from '../../context';
import { formatChord, resolveBindings, type BindableAction } from '../../hotkeys/chords';
import { isMac, useHotkeyRegistry } from '../../hotkeys/HotkeyManager';
import { Limitations } from './limitations';
import '../dialogs.css';

const POINTER_HELP: readonly [string, string][] = [
  ['Drag an item', 'Move'],
  ['Drag a corner handle', 'Resize, keeping proportions (Shift: free)'],
  ['Drag an edge handle', 'Resize one axis'],
  ['Alt + drag a handle', 'Crop'],
  ['Drag the round handle', 'Rotate (Shift: 15° steps)'],
  ['Alt + click', 'Select the next item underneath'],
  ['Shift / Ctrl + click', 'Add to or remove from the selection'],
  ['Drag on empty canvas', 'Marquee select'],
  ['Hold Ctrl while dragging', 'Suspend snapping'],
  ['Mouse wheel', 'Zoom'],
  ['Space + drag, or middle button', 'Pan'],
  ['Double-click an item', 'Open its properties'],
];

export function ShortcutsDialog({ onClose }: { onClose(): void }): JSX.Element {
  const registry = useHotkeyRegistry();
  const stored = useProject((p) => p.hotkeys);
  const bindings = resolveBindings(registry.all as BindableAction[], stored);
  const mac = isMac();
  return (
    <Dialog title="Keyboard and mouse shortcuts" onClose={onClose} width={620} height={600}>
      <div className="ui-section-title">Keyboard</div>
      <table className="ui-table">
        <tbody>
          {registry.all.map((action) => {
            const chords = (bindings[action.id] ?? []).filter((c) => c.includes('Meta') === mac || !/Ctrl|Meta/.test(c));
            const shown = chords.length > 0 ? chords : (bindings[action.id] ?? []);
            return (
              <tr key={action.id}>
                <th scope="row" style={{ fontWeight: 400 }}>
                  {action.label}
                </th>
                <td className="ui-mono">{shown.length > 0 ? shown.map((c) => formatChord(c, mac)).join('  ·  ') : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="ui-section-title">Preview editor</div>
      <table className="ui-table">
        <tbody>
          {POINTER_HELP.map(([gesture, effect]) => (
            <tr key={gesture}>
              <th scope="row" style={{ fontWeight: 400 }}>
                {gesture}
              </th>
              <td>{effect}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="ui-dim">Shortcuts can be changed in Settings → Hotkeys.</p>
    </Dialog>
  );
}

export function AboutDialog({ onClose }: { onClose(): void }): JSX.Element {
  return (
    <Dialog
      title="About"
      onClose={onClose}
      width={560}
      footer={
        <button type="button" className="ui-btn primary" onClick={onClose}>
          Close
        </button>
      }
    >
      <div className="prose">
        <p>
          A live production studio that runs entirely in the browser: scenes and sources, GPU compositing, an audio mixer, WebRTC
          streaming and local recording.
        </p>
        <div className="ui-section-title">Browser limitations</div>
      </div>
      <Limitations />
    </Dialog>
  );
}
